import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"
import { useViewport } from "@/design-system/viewport.tsx"
import { cn } from "@/primitives/utils"

/*
 * beacon: a tooltip waits 400ms, so passing the pointer over a toolbar doesn't flash one, and once
 * one has shown the next opens at once if it's reached within 150ms.
 */
function TooltipProvider({
  delay = 400,
  timeout = 150,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      timeout={timeout}
      {...props}
    />
  )
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  side = "top",
  // beacon: a little further off, with no arrow to bridge the gap.
  sideOffset = 6,
  align = "center",
  alignOffset = 0,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<TooltipPrimitive.Positioner.Props, "align" | "alignOffset" | "side" | "sideOffset">) {
  const { compact } = useViewport()
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        // beacon: on `compact`, keeps 8px clear of the viewport's edge (Base UI's 5px on the desk).
        collisionPadding={compact ? 8 : undefined}
        className="isolate z-50"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            // beacon: dark in both schemes (`--tooltip`), 12px text, lifted by the popover shadow,
            // with no arrow. A Kbd inside inverts onto the dark fill, and a chord's "then" with it,
            // since the faint tier fails contrast there.
            "z-50 flex w-fit max-w-xs origin-(--transform-origin) items-center gap-2 rounded-md bg-tooltip px-2 py-1 text-xs text-tooltip-foreground shadow-popover has-data-[slot=kbd]:pr-1 data-[side=bottom]:slide-in-from-top-1 data-[side=inline-end]:slide-in-from-left-1 data-[side=inline-start]:slide-in-from-right-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 **:data-[slot=kbd]:bg-tooltip-foreground/15 **:data-[slot=kbd]:text-tooltip-foreground **:data-[slot=kbd]:ring-0 **:data-[slot=kbd-then]:text-tooltip-foreground/75 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-open:animate-in data-open:fade-in-0 data-open:duration-150 data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0",
            className,
          )}
          {...props}
        >
          {children}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
